import { EVALUATION_TOOL } from '../../components/AutomationStudio/constant';
import { sanitizePlanStep, sanitizePlanSteps } from '../../components/AutomationStudio/utils/planStepSanitize';
import { filterActiveInstructions } from './planUtils';
import {
    resolveRecordingReplayable,
    resolveRecordingStepStatus,
} from './recordingPlanMarkers';

/**
 * Build Creator-compatible plan JSON from normalized instructions.
 */

export function buildPlanFromInstructions(instructionsList = [], name = 'Automation Studio Test', dynamicResult = null) {
    const steps = [];
    filterActiveInstructions(instructionsList).forEach((inst) => {
        (inst.aiSteps || []).forEach((step) => {
            const status = resolveRecordingStepStatus(step);
            const replayable = resolveRecordingReplayable(step, status);
            const clean = sanitizePlanStep(step) || {};
            steps.push({
                ...clean,
                ...(step.id != null && { id: step.id }),
                ...(step?.stepId && { stepId: step.stepId }),
                stepIndex: steps.length,
                tool: step.tool || '',
                args: step.args ?? step.arguments ?? {},
                executionStrategies: step.executionStrategies || clean.executionStrategies || [],
                instruction: { id: inst.id, text: inst.text || '' },
                description: step.description || step.extractedData?.stepDescription || '',
                extractedData: step.extractedData || { stepDescription: step.description || '' },
                ...(step.timestamp && { timestamp: step.timestamp }),
                ...(step.customStep && { customStep: step.customStep }),
                status,
                ...(typeof replayable === 'boolean' && { replayable }),
                ...(step.executionMode && { executionMode: step.executionMode }),
                ...(step.executionCodeType && { executionCodeType: step.executionCodeType }),
                ...(step.useAssertionCode != null && { useAssertionCode: step.useAssertionCode }),
            });
        });
    });

    const evaluations = steps.filter((s) => s.tool === EVALUATION_TOOL).length;
    return {
        metadata: {
            testName: name,
            createdAt: new Date().toISOString(),
            version: '1.0.0',
            ...(dynamicResult && { dynamicResult }),
        },
        ...(dynamicResult && { dynamicResults: dynamicResult }),
        configuration: {
            stopOnError: true,
            directExecution: true,
            useLLMFallback: true,
        },
        statistics: {
            totalSteps: steps.length,
            failedSteps: 0,
            evaluations,
            completionRate: 0,
        },
        steps,
    };
}

export function activateDynamicCodeStrategiesInPlan(plan) {
    if (!plan || !Array.isArray(plan.steps)) return plan;

    return {
        ...plan,
        steps: plan.steps.map((step) => {
            const strategies = Array.isArray(step.executionStrategies) ? step.executionStrategies : [];
            const hasDynamic = strategies.some((s) => s?.type === 'playwright_dynamic_code');
            const hasCode = strategies.some((s) => s?.type === 'playwright_code');
            if (!hasDynamic || !hasCode) return step;

            const desiredExecutionCodeType = step?.executionCodeType || 'dynamicCode';
            const activateType =
                desiredExecutionCodeType === 'executionModeCode'
                    ? 'playwright_code'
                    : 'playwright_dynamic_code';

            const cloned = strategies.map((s) => ({ ...s }));
            const strategyToActivate = cloned.find((s) => s?.type === activateType);
            if (!strategyToActivate) return step;

            strategyToActivate.enabled = true;
            const newPriority = 0;
            const oldPriority = strategyToActivate.priority;

            const inactiveStrategy = cloned.find(
                (s) =>
                    (s?.type === 'playwright_code' || s?.type === 'playwright_dynamic_code') &&
                    s !== strategyToActivate
            );
            if (inactiveStrategy && inactiveStrategy.priority === newPriority) {
                inactiveStrategy.priority = oldPriority;
            }
            strategyToActivate.priority = newPriority;

            return { ...step, executionStrategies: cloned };
        }),
    };
}

export function buildPlanForSave(instructions, dynamicResult, testCaseKey) {
    const basePlan = buildPlanFromInstructions(
        instructions,
        testCaseKey || 'Automation Studio Test',
        dynamicResult
    );

    const resolvedDynamicResult = dynamicResult || {
        dynamicConversionStatus: 'skipped',
        dynamicConversionMessage: 'Dynamic enrichment skipped.',
        generatedAt: new Date().toISOString(),
    };

    return {
        ...basePlan,
        dynamicResults: resolvedDynamicResult,
        metadata: {
            ...(basePlan.metadata || {}),
            dynamicResult: resolvedDynamicResult,
        },
    };
}

export function getInstructionPayloadToSave(instructions = []) {
    return filterActiveInstructions(instructions)
        .map((inst, idx) => ({
            id: typeof inst.id === 'number' ? inst.id : idx + 1,
            text: inst.text || '',
        }))
        .filter((inst) => inst.text.trim() !== '');
}
